/**
 * Prompts for handing a generated document section to Inky ("Regenerate with Inky", "Ask Inky
 * about this", sources "Fix with Inky"). Inky's edits come back as a review (accept/reject).
 */

/** Longest template instruction quoted in the prefill (the full one is not needed to re-ground). */
const MAX_INSTRUCTION_CHARS = 220;
const MAX_QUOTE_CHARS = 600;
/** Longest current value quoted for an inline field. */
const MAX_INLINE_QUOTE_CHARS = 160;

/**
 * One-click guidance for a regenerate handoff. Each appends its text after "Guidance:" (or after
 * the user's own guidance, as a new sentence) and sends; "Send as is" sends the request without
 * extra guidance (an empty trailing "Guidance:" is dropped).
 */
export const REGENERATE_GUIDANCE_CHIPS = [
  { label: 'Shorter', append: 'Make it shorter.' },
  { label: 'More detail', append: 'Add more detail.' },
  { label: 'More formal', append: 'Use a more formal tone.' },
  { label: 'Plainer language', append: 'Use plainer language.' },
  { label: 'Use the latest meetings', append: 'Base it mainly on the latest meetings.' },
  { label: 'Send as is' },
];

const oneLine = (text, max) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

/**
 * How to name a section in a prompt.
 * @param {string} [heading] - Section heading.
 * @returns {string} `the "Scope" section` or `this section`.
 */
const sectionName = (heading) => {
  const name = oneLine(heading, 120);
  return name ? `the "${name}" section` : 'this section';
};

/**
 * Prefill for "Regenerate with Inky" / "Fill this section with Inky". The host selects the
 * section (or inline field) in the editor first, so the request is worded around the selection;
 * internal ids never appear. Not sent: it ends with "Guidance: " so the user can say what should
 * change (or pick a guidance chip).
 * @param {{heading?: string, status?: string|null, needsInput?: string|null, instruction?: string|null, inline?: boolean, currentValue?: string}} section - Section info.
 * @returns {string} Prefill text.
 */
export function buildRegeneratePrompt({ heading, status, needsInput, instruction, inline = false, currentValue = '' } = {}) {
  const name = oneLine(heading, 120);
  const fill = status === 'needs_input';
  const verb = fill ? 'Fill' : 'Regenerate';
  const lines = [];
  if (inline) {
    const where = name ? ` in the "${name}" section` : '';
    const value = oneLine(currentValue, MAX_INLINE_QUOTE_CHARS);
    lines.push(`${verb} the selected field${where} of this document${fill && needsInput ? `, which still needs: ${oneLine(needsInput, 200)}` : ''}.`);
    lines.push(`Replace only its current value${value ? ` ("${value}")` : ''} and leave the surrounding text unchanged.`);
    lines.push(fill
      ? "Write it from the project's requirements and meetings, and show the result as a change I can accept or reject."
      : "Write a fresh value from the project's requirements and meetings, not a light edit of the current wording, and show it as a change I can accept or reject.");
  } else {
    const target = name ? `the selected "${name}" section` : 'the selected section';
    lines.push(`${verb} ${target} of this document${fill && needsInput ? `, which still needs: ${oneLine(needsInput, 200)}` : ''}.`);
    lines.push(fill
      ? "Write it fresh from the project's requirements and meetings, and show the result as a change I can accept or reject."
      : "Write a fresh version from the project's requirements and meetings, not a light edit of the current wording, and show it as a change I can accept or reject.");
  }
  if (instruction) lines.push(`(Template instruction: ${oneLine(instruction, MAX_INSTRUCTION_CHARS)})`);
  lines.push('Guidance: ');
  return lines.join('\n');
}

/** A trailing "Guidance:" with nothing after it. */
const EMPTY_GUIDANCE = /\n?\s*Guidance:\s*$/;

/**
 * The message to send from a composer that may hold a handoff prefill: drops an empty trailing
 * "Guidance:", and adds a guidance chip's text after "Guidance:" or, when the user already wrote
 * guidance, as a new sentence ("keep the table. Make it shorter.").
 * @param {string} text - Composer text.
 * @param {string} [append] - Chip guidance.
 * @returns {string} Message.
 */
export function composeGuidedMessage(text, append = '') {
  const base = String(text || '').trim();
  const extra = String(append || '').trim();
  if (!extra) return base.replace(EMPTY_GUIDANCE, '').trim();
  if (/(?:^|\n)\s*Guidance:$/.test(base)) return `${base} ${extra}`;
  if (!base) return extra;
  return /[.!?:;]$/.test(base) ? `${base} ${extra}` : `${base}. ${extra}`;
}

/**
 * Prefill for "Ask Inky about this": the section quoted, then a question for the user to finish.
 * @param {{heading?: string, text?: string}} section - Section info.
 * @returns {string} Prefill text (never sent automatically).
 */
export function buildAskAboutSectionPrompt({ heading, text } = {}) {
  const quote = oneLine(text, MAX_QUOTE_CHARS);
  const ask = `About ${sectionName(heading)}: `;
  return quote ? `> ${quote}\n\n${ask}` : ask;
}

/**
 * "Fix with Inky" for a source item that needs a source. Sent straight away (a complete request).
 * @param {{heading?: string, goalPlain?: string, comment?: string}} item - Source item.
 * @returns {string} Prompt.
 */
export function buildSourceFixPrompt({ heading, goalPlain, comment } = {}) {
  const name = sectionName(heading || goalPlain);
  const note = comment ? ` The note on it says: "${oneLine(comment, 200)}".` : '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} of this document needs a source.${note} Check it against the project's requirements and meeting sessions: cite the ones that support it, and rewrite or flag any claim they do not support. Show the changes for review.`;
}
